export interface DashboardMetrics {
  wallet: {
    balance: number;
    monthlyChange: number;
    currency: string;
  };
  governance: {
    activeProposals: number;
    participationRate: number;
    userVotingScore: number;
    nextElection: number;
  };
  events: {
    upcoming: {
      title: string;
      date: string;
      attendees: number;
    }[];
    nextEvent: {
      title: string;
      date: string;
      time: string;
      attendees: number;
    } | null;
  };
  messages: {
    unreadCount: number;
    recentMessages: {
      id: string;
      subject: string;
      priority: 'high' | 'normal' | 'low';
      timestamp: string;
    }[];
  };
  treasury: {
    totalBalance: number;
    monthlyCollection: number;
    collectionRate: number;
    recentExpense: number;
    recentExpenseDescription: string;
  };
  vendor?: {
    workOrders: {
      active: number;
      pending: number;
      completed: number;
    };
    payments: {
      due: number;
      paid: number;
    };
    schedule: {
      nextJob: {
        title: string;
        date: string;
        time: string;
        location: string;
      } | null;
    };
    ratings: {
      average: number;
      totalReviews: number;
    };
  };
  notifications: {
    count: number;
    recent: {
      id: string;
      title: string;
      description: string;
      priority: 'high' | 'normal' | 'low';
      type: 'governance' | 'payment' | 'maintenance' | 'event' | 'general';
      timestamp: string;
      read: boolean;
    }[];
  };
}

export interface DashboardResponse {
  success: boolean;
  data?: DashboardMetrics;
  error?: string;
}

export interface NotificationResponse {
  success: boolean;
  error?: string;
}

class DashboardService {
  private baseUrl = '/api/dashboard';

  async getDashboardMetrics(userRole?: string): Promise<DashboardResponse> {
    try {
      // For now, return demo data since backend isn't ready
      return {
        success: true,
        data: userRole === 'vendor' ? this.getVendorDemoData() : this.getDemoData(),
      };

      // When backend is ready, use this:
      // const response = await fetch(`${this.baseUrl}/metrics?role=${userRole}`);
      // const data = await response.json();
      // return data;
    } catch (error) {
      console.error('Dashboard service error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async markNotificationAsRead(notificationId: string): Promise<NotificationResponse> {
    try {
      // Backend implementation placeholder
      console.log('Marking notification as read:', notificationId);
      return { success: true };

      // When backend is ready:
      // const response = await fetch(`${this.baseUrl}/notifications/${notificationId}/read`, {
      //   method: 'PATCH',
      // });
      // return response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  async addSampleNotifications(): Promise<NotificationResponse> {
    try {
      // Backend implementation placeholder
      console.log('Adding sample notifications');
      return { success: true };

      // When backend is ready:
      // const response = await fetch(`${this.baseUrl}/notifications/sample`, {
      //   method: 'POST',
      // });
      // return response.json();
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  getDemoData(): DashboardMetrics {
    return {
      wallet: {
        balance: 2847.5,
        monthlyChange: 125.0,
        currency: 'USD',
      },
      governance: {
        activeProposals: 3,
        participationRate: 72,
        userVotingScore: 95,
        nextElection: 45,
      },
      events: {
        upcoming: [
          {
            title: 'Community BBQ',
            date: '2025-08-08',
            attendees: 42,
          },
          {
            title: 'Board Meeting',
            date: '2025-08-15',
            attendees: 12,
          },
        ],
        nextEvent: {
          title: 'Community BBQ',
          date: '2025-08-08',
          time: '6:00 PM',
          attendees: 42,
        },
      },
      messages: {
        unreadCount: 7,
        recentMessages: [
          {
            id: '1',
            subject: 'Board Meeting Notes',
            priority: 'high',
            timestamp: '2025-08-07T10:00:00Z',
          },
          {
            id: '2',
            subject: 'Pool Schedule Update',
            priority: 'normal',
            timestamp: '2025-08-07T08:30:00Z',
          },
        ],
      },
      treasury: {
        totalBalance: 847250,
        monthlyCollection: 34800,
        collectionRate: 96,
        recentExpense: 12450,
        recentExpenseDescription: 'Landscaping maintenance',
      },
      vendor: {
        workOrders: {
          active: 8,
          pending: 3,
          completed: 45,
        },
        payments: {
          due: 2450.0,
          paid: 18750.0,
        },
        schedule: {
          nextJob: {
            title: 'Pool Maintenance',
            date: '2025-01-25',
            time: '2:00 PM',
            location: 'Community Pool',
          },
        },
        ratings: {
          average: 4.7,
          totalReviews: 23,
        },
      },
      notifications: {
        count: 6,
        recent: [
          {
            id: '1',
            title: 'New Proposal Submitted',
            description: 'Pool renovation proposal requires your vote',
            priority: 'high',
            type: 'governance',
            timestamp: '2025-08-07T12:00:00Z',
            read: false,
          },
          {
            id: '2',
            title: 'Payment Processed',
            description: 'HOA dues payment of $485.00 successfully processed',
            priority: 'normal',
            type: 'payment',
            timestamp: '2025-08-06T14:30:00Z',
            read: false,
          },
          {
            id: '3',
            title: 'Work Order Update',
            description: 'Gym equipment maintenance scheduled for tomorrow',
            priority: 'low',
            type: 'maintenance',
            timestamp: '2025-08-05T16:45:00Z',
            read: false,
          },
          {
            id: '4',
            title: 'Event Reminder',
            description: 'Community BBQ starts in 24 hours',
            priority: 'normal',
            type: 'event',
            timestamp: '2025-08-04T09:15:00Z',
            read: false,
          },
          {
            id: '5',
            title: 'Board Meeting Minutes',
            description: 'October board meeting minutes are now available',
            priority: 'low',
            type: 'general',
            timestamp: '2025-07-31T11:00:00Z',
            read: false,
          },
        ],
      },
    };
  }

  getVendorDemoData(): DashboardMetrics {
    return {
      wallet: {
        balance: 2450.0,
        monthlyChange: 850.0,
        currency: 'USD',
      },
      governance: {
        activeProposals: 0,
        participationRate: 0,
        userVotingScore: 0,
        nextElection: 0,
      },
      events: {
        upcoming: [],
        nextEvent: null,
      },
      messages: {
        unreadCount: 3,
        recentMessages: [
          {
            id: '1',
            subject: 'Work Order Assignment',
            priority: 'high',
            timestamp: '2025-08-07T10:00:00Z',
          },
          {
            id: '2',
            subject: 'Payment Confirmation',
            priority: 'normal',
            timestamp: '2025-08-07T08:30:00Z',
          },
        ],
      },
      treasury: {
        totalBalance: 0,
        monthlyCollection: 0,
        collectionRate: 0,
        recentExpense: 0,
        recentExpenseDescription: '',
      },
      vendor: {
        workOrders: {
          active: 8,
          pending: 3,
          completed: 45,
        },
        payments: {
          due: 2450.0,
          paid: 18750.0,
        },
        schedule: {
          nextJob: {
            title: 'Pool Maintenance',
            date: '2025-01-25',
            time: '2:00 PM',
            location: 'Community Pool',
          },
        },
        ratings: {
          average: 4.7,
          totalReviews: 23,
        },
      },
      notifications: {
        count: 5,
        recent: [
          {
            id: 'v1',
            title: 'New Work Order Assigned',
            description: 'Pool maintenance scheduled for tomorrow at 2:00 PM',
            priority: 'high',
            type: 'maintenance',
            timestamp: '2025-08-07T12:00:00Z',
            read: false,
          },
          {
            id: 'v2',
            title: 'Payment Received',
            description: 'Service payment of $850.00 has been processed',
            priority: 'normal',
            type: 'payment',
            timestamp: '2025-08-06T14:30:00Z',
            read: false,
          },
          {
            id: 'v3',
            title: 'Service Request Update',
            description: 'Emergency HVAC repair request - urgent response needed',
            priority: 'high',
            type: 'maintenance',
            timestamp: '2025-08-05T16:45:00Z',
            read: false,
          },
          {
            id: 'v4',
            title: 'Schedule Change',
            description: 'Tennis court maintenance moved to next Tuesday',
            priority: 'normal',
            type: 'general',
            timestamp: '2025-08-04T09:15:00Z',
            read: false,
          },
          {
            id: 'v5',
            title: 'New Group Buy Opportunity',
            description: 'Bulk HVAC filter order - 40% discount available',
            priority: 'low',
            type: 'general',
            timestamp: '2025-07-31T11:00:00Z',
            read: false,
          },
        ],
      },
    };
  }
}

export const dashboardService = new DashboardService();
